/*
 * KINARI デザインプロトタイプ (design/*.html, dc-runtime) のロジックを React なしで動かす最小ランタイム。
 *
 * scripts/build-theme.mjs がテンプレートの {{ binding }} を次の属性に変換している:
 *   data-dc-a="data-open:menuOpen src:ln.img"      … 属性バインディング (キーは ln.img のようにドット区切りも可)
 *   data-dc-t="loginLabel"                         … テキストバインディング
 *   data-dc-on="click:toggleMenu"                  … イベントハンドラ
 *   data-dc-ref="wfRef"                            … ref
 *   <template data-dc-if="cartHas">                … <sc-if value="{{ cartHas }}">
 *   <template data-dc-for="cartLines" data-dc-as="ln"> … <sc-for list="{{ cartLines }}" as="ln">
 * ページ側の `class Component extends DCLogic` はそのまま実行し、setState のたびに
 * renderVals() を評価して上記の属性・テキスト・条件分岐・繰り返しへ反映する。
 */
(() => {
  class DCLogic {
    constructor(props) {
      this.props = props || {};
      this.state = {};
    }
    setState(update, cb) {
      this.__host && this.__host.setState(update, cb);
    }
    forceUpdate() {
      this.__host && this.__host.schedule();
    }
    componentDidMount() {}
    componentDidUpdate() {}
    componentWillUnmount() {}
    renderVals() {
      return {};
    }
  }

  const React = { createRef: () => ({ current: null }) };
  const factories = {};
  const extenders = {};

  const within = (root, sel) => [...(root.matches(sel) ? [root] : []), ...root.querySelectorAll(sel)];
  const pairs = (s) =>
    s
      .trim()
      .split(/\s+/)
      .map((p) => {
        const i = p.indexOf(':');
        return [p.slice(0, i), p.slice(i + 1)];
      });
  const get = (scope, key) => key.split('.').reduce((o, k) => (o == null ? undefined : o[k]), scope);
  // 英語のページでは、ページのロジックが出す日本語を置き換え表 (snippets/kinari-i18n.liquid の window.KINARI_I18N) で英語にする。
  // 丸ごと一致するものだけを置き換え、組み立てた文字列 (「配送希望日：…」など) は patterns の正規表現で置き換える
  const I18N = window.KINARI_I18N || null;
  const PATTERNS = I18N ? (I18N.patterns || []).map(([re, to]) => [new RegExp(re), to]) : [];
  const tr = (s) => {
    if (!I18N || !s) return s;
    if (Object.prototype.hasOwnProperty.call(I18N.map, s)) return I18N.map[s];
    for (const [re, to] of PATTERNS) if (re.test(s)) return s.replace(re, to);
    return s;
  };
  const TR_ATTRS = new Set(['placeholder', 'aria-label', 'alt', 'title']);
  // React と同じく、data-* / aria-* 以外の属性 (open, disabled など) は true で空の属性、false で属性なしにする
  const attrValue = (attr, v) => {
    if (v == null) return null;
    if (typeof v === 'boolean' && !/^(data|aria)-/.test(attr)) return v ? '' : null;
    return String(v);
  };

  // nodes (要素とその子孫) のバインディングを集め、update(scope) で反映する。
  // <template> の中身は DOM ツリーの外 (template.content) にあるため querySelectorAll に拾われない。
  // 条件分岐・繰り返しはそれぞれの <template> をコメントの目印に置き換え、描画のたびに複製を出し入れする
  function bind(nodes) {
    const els = nodes.filter((n) => n.nodeType === 1);
    const all = (sel) => els.flatMap((el) => within(el, sel));
    const attrEls = all('[data-dc-a]').map((el) => [el, pairs(el.getAttribute('data-dc-a'))]);
    const textEls = all('[data-dc-t]').map((el) => [el, el.getAttribute('data-dc-t')]);
    const onEls = all('[data-dc-on]');
    const refEls = all('[data-dc-ref]');
    const blocks = all('template[data-dc-if], template[data-dc-for]').map((tpl) => {
      const anchor = document.createComment(tpl.hasAttribute('data-dc-if') ? 'dc-if' : 'dc-for');
      tpl.replaceWith(anchor);
      return {
        anchor,
        content: tpl.content,
        ifKey: tpl.getAttribute('data-dc-if'),
        forKey: tpl.getAttribute('data-dc-for'),
        as: tpl.getAttribute('data-dc-as') || 'item',
        items: [],
      };
    });

    let scope = {};
    for (const el of onEls) {
      for (const [type, key] of pairs(el.getAttribute('data-dc-on'))) {
        el.addEventListener(type, (e) => {
          const fn = get(scope, key);
          if (typeof fn === 'function') fn(e);
        });
      }
    }

    const add = (block) => {
      const frag = block.content.cloneNode(true);
      const item = { nodes: [...frag.childNodes] };
      item.view = bind(item.nodes);
      block.anchor.before(frag);
      block.items.push(item);
      return item;
    };
    const drop = (block, from) => {
      for (const item of block.items.splice(from)) item.nodes.forEach((n) => n.remove());
    };

    let refsDone = false;
    return {
      update(next) {
        scope = next;
        for (const [el, list] of attrEls) {
          for (const [attr, key] of list) {
            const raw = attrValue(attr, get(scope, key));
            const v = TR_ATTRS.has(attr) ? tr(raw) : raw;
            if (v == null) el.removeAttribute(attr);
            else if (el.getAttribute(attr) !== v) el.setAttribute(attr, v);
          }
        }
        for (const [el, key] of textEls) {
          const raw = get(scope, key);
          const v = raw == null || typeof raw === 'boolean' ? '' : tr(String(raw));
          if (el.textContent !== v) el.textContent = v;
        }
        for (const block of blocks) {
          if (block.ifKey) {
            if (!get(scope, block.ifKey)) drop(block, 0);
            else (block.items[0] || add(block)).view.update(scope);
            continue;
          }
          const list = get(scope, block.forKey);
          const arr = Array.isArray(list) ? list : [];
          drop(block, arr.length);
          arr.forEach((v, i) => (block.items[i] || add(block)).view.update({ ...scope, [block.as]: v, $index: i }));
        }
        if (!refsDone) {
          refsDone = true;
          for (const el of refEls) {
            const ref = get(scope, el.getAttribute('data-dc-ref'));
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }
        }
      },
    };
  }

  function mount(root, name) {
    let Component = factories[name](DCLogic, React);
    for (const ext of [...(extenders['*'] || []), ...(extenders[name] || [])]) Component = ext(Component);

    let props = {};
    try {
      props = JSON.parse(root.getAttribute('data-dc-props') || '{}');
    } catch (e) {
      console.warn('[kinari-dc] data-dc-props', e);
    }
    const logic = new Component(props);
    const view = bind([root]);
    let scheduled = false;
    let callbacks = [];
    let prevState = logic.state;

    const render = () => view.update({ ...logic.props, ...(logic.renderVals() || {}) });

    const flush = () => {
      scheduled = false;
      const cbs = callbacks;
      callbacks = [];
      const prev = prevState;
      prevState = logic.state;
      render();
      try {
        logic.componentDidUpdate(logic.props, prev);
      } catch (e) {
        console.error(e);
      }
      cbs.forEach((cb) => cb.call(logic));
    };

    logic.__host = {
      setState(update, cb) {
        const prev = logic.state;
        const patch = typeof update === 'function' ? update(prev) : update;
        logic.state = { ...prev, ...patch };
        if (cb) callbacks.push(cb);
        this.schedule();
      },
      schedule() {
        if (scheduled) return;
        scheduled = true;
        queueMicrotask(flush);
      },
    };

    render();
    try {
      logic.componentDidMount();
    } catch (e) {
      console.error(e);
    }
    return logic;
  }

  const mountAll = () => {
    document.querySelectorAll('[data-dc-root]').forEach((root) => {
      if (root.__dc) return;
      const name = root.getAttribute('data-dc-root');
      if (!factories[name]) return;
      root.__dc = mount(root, name);
    });
  };

  // ページのロジックから参照される画像 (design/uploads/) は、セクションが出力する
  // <script type="application/json" data-kinari-assets> の { ファイル名: asset_url } で引く
  let assets = null;
  const asset = (file) => {
    if (!assets) {
      assets = {};
      document.querySelectorAll('script[data-kinari-assets]').forEach((s) => {
        try {
          Object.assign(assets, JSON.parse(s.textContent));
        } catch (e) {
          console.warn('[kinari-dc] data-kinari-assets', e);
        }
      });
    }
    return assets[file] || file;
  };

  window.KinariDC = {
    DCLogic,
    define(name, factory) {
      factories[name] = factory;
    },
    extend(name, ext) {
      (extenders[name] = extenders[name] || []).push(ext);
    },
    asset,
    mountAll,
  };

  // このスクリプトは defer で読み込むため readyState は 'interactive' になっている。ページ側の
  // kinari-<page>.js (同じく defer) が define し終えるのを DOMContentLoaded で待つ。load は保険 (mountAll は冪等)
  if (document.readyState === 'complete') queueMicrotask(mountAll);
  else {
    document.addEventListener('DOMContentLoaded', mountAll);
    addEventListener('load', mountAll);
  }

  // テーマエディタでセクションが再描画されたら、新しい DOM にマウントし直す
  document.addEventListener('shopify:section:load', mountAll);
})();
